/**
 * `#connect=<base64url JSON {apiUrl, streamKey, slug}>`: the portal's "Open Stream Manager in OhMyStudio"
 * link. It carries the stream key, so it travels in the fragment (never sent to a server) and is removed
 * from the address bar as soon as the studio reads it.
 */
import { cleanPortalUrl } from '../core/channel';
import type { ChannelSettings, StreamingSettings } from '../core/model';
import { fetchChannel, type ChannelApiDeps } from './channel-api';
import { parseStreamKey } from './swarm-publisher';
import { isTokenSafeEndpoint } from './whip-http';

export interface ConnectLink {
  apiUrl: string;
  streamKey: string;
  slug: string;
  /** The portal's origin (https, or http on loopback), for the Stream Manager chat and channel links; null when absent or unsafe. */
  portalUrl: string | null;
}

const SLUG_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

function decodeBase64Url(text: string): string | null {
  if (!/^[A-Za-z0-9_-]+={0,2}$/.test(text)) return null;
  try {
    const b64 = text.replace(/=+$/, '').replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
    return new TextDecoder('utf-8', { fatal: true }).decode(Uint8Array.from(binary, (c) => c.charCodeAt(0)));
  } catch {
    return null;
  }
}

/** The link's portal origin when it is https or loopback http (cleanPortalUrl answers the default for anything else). */
function linkPortalUrl(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  try {
    const origin = new URL(v.trim()).origin;
    return cleanPortalUrl(v) === origin ? origin : null;
  } catch {
    return null;
  }
}

/** The link's payload, or null when it is not valid base64url JSON with a safe API URL, a stream key and a slug. */
export function parseConnectPayload(encoded: string): ConnectLink | null {
  const json = decodeBase64Url(encoded.trim());
  if (json === null) return null;
  let data: unknown;
  try {
    data = JSON.parse(json);
  } catch {
    return null;
  }
  if (!data || typeof data !== 'object') return null;
  const { apiUrl, streamKey, slug, portalUrl } = data as Record<string, unknown>;
  if (typeof apiUrl !== 'string' || typeof streamKey !== 'string' || typeof slug !== 'string') return null;
  const root = apiUrl.trim().replace(/\/+$/, '');
  if (!isTokenSafeEndpoint(root)) return null;
  if (!parseStreamKey(streamKey)) return null;
  if (!SLUG_PATTERN.test(slug.trim())) return null;
  return { apiUrl: root, streamKey: streamKey.trim(), slug: slug.trim(), portalUrl: linkPortalUrl(portalUrl) };
}

export interface ConnectFragment {
  /** False when the fragment has no `connect=` parameter. */
  present: boolean;
  /** The parsed link, or null when it is present but invalid. */
  link: ConnectLink | null;
}

/** Rebuilds the standalone link after startup stripped the private connection fragment. */
export function standaloneStudioUrl(href: string, link: ConnectLink | null): string {
  const url = new URL(href);
  url.hash = '';
  if (link) {
    const bytes = new TextEncoder().encode(JSON.stringify(link));
    const payload = btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    url.hash = new URLSearchParams({ connect: payload }).toString();
  }
  return url.href;
}

type LocationLike = Pick<Location, 'hash' | 'pathname' | 'search'>;
type HistoryLike = Pick<History, 'replaceState' | 'state'>;

/** Where `omp:show-dock` (and `#dock=`) sends the studio: the Stream Manager, its Collaboration card, or the Streaming tools dialog. */
export type ShowDock = 'stream-manager' | 'collab' | 'tools';

export function isShowDock(value: unknown): value is ShowDock {
  return value === 'stream-manager' || value === 'collab' || value === 'tools';
}

/** What the studio reads from its address fragment at load. */
export interface StudioFragment {
  connect: ConnectFragment;
  /** `theme=light|dark`: the starting theme chosen by whoever opened the studio; null when absent or unknown. */
  theme: 'light' | 'dark' | null;
  /** `embed=1`: the studio runs inside the portal's creator dashboard. */
  embedded: boolean;
  /** `dock=stream-manager|collab|tools`: what to bring up once the dock is ready; null when absent or unknown. */
  dock: ShowDock | null;
}

// Private startup captures before authorization; only authorized enrollment retains a handoff.
const startupFragments = new WeakMap<LocationLike, StudioFragment>();
export function retainStudioFragmentForStartup(location: LocationLike, fragment: StudioFragment): void {
  startupFragments.set(location, fragment);
}

const FRAGMENT_KEYS = ['connect', 'theme', 'embed', 'dock'] as const;

/**
 * Reads `connect=`, `theme=`, `embed=` and `dock=` from the fragment and removes them from the address bar in one
 * history replacement (other fragment parameters stay). `connect=` goes whether or not it is valid: the
 * stream key must not linger in the URL or the history.
 */
export function takeStudioFragment(location: LocationLike, history: HistoryLike): StudioFragment {
  const retained = startupFragments.get(location);
  startupFragments.delete(location);
  const params = new URLSearchParams(location.hash.replace(/^#/, ''));
  const encoded = params.get('connect');
  const themeParam = params.get('theme');
  const embedParam = params.get('embed');
  const theme = themeParam === 'light' || themeParam === 'dark' ? themeParam : null;
  const embedded = embedParam === '1' || embedParam === 'true';
  const dockParam = params.get('dock');
  const dock = isShowDock(dockParam) ? dockParam : null;
  if (FRAGMENT_KEYS.some((key) => params.has(key))) {
    for (const key of FRAGMENT_KEYS) params.delete(key);
    const rest = params.toString();
    history.replaceState(history.state, '', `${location.pathname}${location.search}${rest ? `#${rest}` : ''}`);
  }
  if (retained) return retained;
  const connect: ConnectFragment = encoded === null ? { present: false, link: null } : { present: true, link: parseConnectPayload(encoded) };
  return { connect, theme, embedded, dock };
}

/** `takeStudioFragment` for callers that only want the connect link (it still strips `theme=` and `embed=`). */
export function takeConnectLink(location: LocationLike, history: HistoryLike): ConnectFragment {
  return takeStudioFragment(location, history).connect;
}

/** The parts of the studio a connect link touches. */
export interface ConnectTarget {
  commands: {
    setStreamingSettings(patch: Partial<StreamingSettings>): void;
    setChannelSettings(patch: Partial<Pick<ChannelSettings, 'portalUrl'>>): void;
  };
  notify(level: 'info' | 'warn' | 'error', message: string): void;
}

/**
 * Stores the API URL and stream key the way Settings → Stream does (swarm mode, so going live and the
 * Stream Manager use them), then names the channel in a notice. Resolves with the name shown.
 */
export async function applyConnectLink(studio: ConnectTarget, link: ConnectLink, deps: ChannelApiDeps = {}): Promise<string> {
  studio.commands.setStreamingSettings({ mode: 'swarm', swarmApiUrl: link.apiUrl, swarmStreamKey: link.streamKey });
  if (link.portalUrl) studio.commands.setChannelSettings({ portalUrl: link.portalUrl });
  let name = link.slug;
  try {
    const channel = await fetchChannel({ apiUrl: link.apiUrl, slug: link.slug }, deps);
    name = channel?.displayName || channel?.name || link.slug;
  } catch {
    // The settings are stored; the notice falls back to the slug.
  }
  studio.notify('info', `Connected to ${name}`);
  return name;
}
